import type { Course, CourseMap, LatLng } from "../../shared/types.js";
import type { ClientOp, TournamentSnapshot } from "../../shared/protocol.js";

/** Admin and player are earned by claiming a name off the roster; spectator is the
 * read-only third door, opened by the join code alone. */
export type Role = "admin" | "player" | "spectator";

export interface KnownTournament {
  code: string;
  name: string;
  role: Role;
}

export interface AuthRecord {
  token: string;
  role: Role;
  /**
   * Who this device's scores belong to.
   *
   * For an admin this is the roster seat the organiser plays under, if they took one —
   * mirrored down from the tournament on every state frame (see `identity.ts`), not
   * decided here. Their token stays the admin token either way; the seat only says
   * which name the scores land on.
   *
   * Always null for a spectator: the token is shared by everyone watching, so there
   * is nobody in particular for it to be.
   */
  playerId: string | null;
  savedAt: number;
}

/**
 * Which hat an organiser is currently wearing on *this* device. Meaningless for anyone
 * else — a player has only ever been a player.
 *
 * Device-local on purpose: it is a view preference, not a fact about the game, and two
 * of the organiser's own phones can sensibly disagree. The seat it plays under is the
 * opposite — that belongs to the tournament, so it survives a new phone.
 */
export type AppMode = "organizer" | "player";

/** Long enough to span a multi-day tournament without forcing re-entry (this app is
 * explicitly offline-tolerant), short enough that a shared/kiosk device doesn't keep
 * access forever once nobody's actively using it. */
const AUTH_TTL_MS = 14 * 24 * 60 * 60 * 1000;

export interface QueuedOp {
  clientOpId: string;
  op: ClientOp;
}

const KNOWN_KEY = "golf:known";
const AUTH_PREFIX = "golf:auth:";
const CACHE_PREFIX = "golf:cache:";
const OUTBOX_PREFIX = "golf:outbox:";
const COURSE_CACHE_PREFIX = "golf:course:";
const MAP_CACHE_PREFIX = "golf:map:";
const MODE_PREFIX = "golf:mode:";
const LAST_ROUND_PREFIX = "golf:lastRound:";
const LAST_HOLE_PREFIX = "golf:lastHole:";

function readJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJSON(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage full/unavailable — the WS connection is still the source of truth
  }
}

function remove(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    // storage unavailable; there was nothing stored to remove either
  }
}

export function getKnownTournaments(): KnownTournament[] {
  return readJSON<KnownTournament[]>(KNOWN_KEY, []);
}

export function rememberTournament(entry: KnownTournament) {
  const known = getKnownTournaments().filter((t) => t.code !== entry.code);
  known.unshift(entry);
  writeJSON(KNOWN_KEY, known.slice(0, 20));
}

/** Drops every local trace of a tournament — used after deleting one, so it stops
 * showing on the home screen and its cached state can't be replayed. */
export function forgetTournament(code: string) {
  writeJSON(KNOWN_KEY, getKnownTournaments().filter((t) => t.code !== code));
  for (const prefix of [AUTH_PREFIX, CACHE_PREFIX, OUTBOX_PREFIX, MODE_PREFIX, LAST_ROUND_PREFIX]) {
    try {
      localStorage.removeItem(prefix + code);
    } catch {
      // storage unavailable — nothing to clean up
    }
  }
  // One key per round rather than one per tournament, so this can't be removed by name.
  try {
    const stale = Object.keys(localStorage).filter((k) => k.startsWith(LAST_HOLE_PREFIX + code + ":"));
    for (const key of stale) localStorage.removeItem(key);
  } catch {
    // storage unavailable — nothing to clean up
  }
}

/** How stale the stamp may get before a read refreshes it. Only there to keep this off
 * localStorage on every render — `getAuth` is called from the app bar. */
const AUTH_REFRESH_AFTER_MS = 24 * 60 * 60 * 1000;

export function getAuth(code: string): AuthRecord | null {
  const auth = readJSON<AuthRecord | null>(AUTH_PREFIX + code, null);
  if (!auth) return null;
  const age = Date.now() - (auth.savedAt ?? 0);
  if (age > AUTH_TTL_MS) {
    try {
      localStorage.removeItem(AUTH_PREFIX + code);
    } catch {
      // storage unavailable — nothing to clean up
    }
    return null;
  }
  // The TTL is meant to expire access "once nobody's actively using it", but the stamp
  // was only ever written at join time — so it counted down from then regardless, and a
  // tournament lives sixty days against this fourteen. A player still opening the app
  // daily was dropped mid-event, off a home screen that went on listing the game. A
  // sliding window is what the comment on AUTH_TTL_MS already describes.
  if (age > AUTH_REFRESH_AFTER_MS) writeJSON(AUTH_PREFIX + code, { ...auth, savedAt: Date.now() });
  return auth;
}

export function setAuth(code: string, auth: Omit<AuthRecord, "savedAt">) {
  writeJSON(AUTH_PREFIX + code, { ...auth, savedAt: Date.now() });
}

export function getCache(code: string): TournamentSnapshot | null {
  return readJSON<TournamentSnapshot | null>(CACHE_PREFIX + code, null);
}

export function setCache(code: string, snapshot: TournamentSnapshot) {
  writeJSON(CACHE_PREFIX + code, snapshot);
}

export function getOutbox(code: string): QueuedOp[] {
  return readJSON<QueuedOp[]>(OUTBOX_PREFIX + code, []);
}

export function setOutbox(code: string, ops: QueuedOp[]) {
  writeJSON(OUTBOX_PREFIX + code, ops);
}

/** Courses are permanent library data, cached client-side too so the scorecard
 * (par/stroke index) still renders with no signal (spec §7/§8). */
export function getCourseCache(id: string): Course | null {
  return readJSON<Course | null>(COURSE_CACHE_PREFIX + id, null);
}

export function setCourseCache(course: Course) {
  writeJSON(COURSE_CACHE_PREFIX + course.id, course);
}

/**
 * Hand-traced course geometry, cached the same way and for a stronger reason: the
 * distances it feeds are wanted exactly where there's no signal. Keyed by mapId and
 * stamped with the blob SHA it came from, so a map that's been re-traced is refetched
 * rather than served stale forever.
 */
export function getMapCache(mapId: string): { map: CourseMap; sha: string } | null {
  return readJSON<{ map: CourseMap; sha: string } | null>(MAP_CACHE_PREFIX + mapId, null);
}

export function setMapCache(map: CourseMap, sha: string) {
  writeJSON(MAP_CACHE_PREFIX + map.mapId, { map, sha });
}

const GPS_KEY = "golf:gps";

/**
 * Whether this player has asked for distances to the green.
 *
 * Remembered so the location prompt is a one-time thing rather than a tap at the start
 * of every round. Off by default — nothing about the app should turn the GPS on until
 * someone has actually asked for it.
 */
export function getGpsEnabled(): boolean {
  return readJSON<boolean>(GPS_KEY, false) === true;
}

export function setGpsEnabled(on: boolean) {
  writeJSON(GPS_KEY, on);
}

/** Which theme the app should use. "system" — the default — follows the phone. */
export type ThemePref = "system" | "light" | "dark";

const THEME_KEY = "golf:theme";

export function getThemePref(): ThemePref {
  const pref = readJSON<ThemePref>(THEME_KEY, "system");
  return pref === "light" || pref === "dark" ? pref : "system";
}

export function setThemePref(pref: ThemePref) {
  writeJSON(THEME_KEY, pref);
}

/** Remembers the round you were last scoring in each tournament, so the "Scorecard"
 * tab returns you to that hole-by-hole screen rather than a generic list. */
export function getLastRound(code: string): string | null {
  return readJSON<string | null>(LAST_ROUND_PREFIX + code, null);
}

export function setLastRound(code: string, roundId: string) {
  writeJSON(LAST_ROUND_PREFIX + code, roundId);
}

/**
 * Which hole you were on in a given round, so the scorecard resumes there.
 *
 * The scorecard is one of three tabs and doesn't survive a visit to the standings —
 * without this, checking where you stand on the 16th puts you back on the 1st tee, with
 * fifteen taps to get home. Per round, because a tournament's rounds are each played to
 * their own point, and a hole number rather than a position on the card (see
 * `resumeHoleIndex`).
 */
export function getLastHole(code: string, roundId: string): number | null {
  const hole = readJSON<number | null>(`${LAST_HOLE_PREFIX}${code}:${roundId}`, null);
  return typeof hole === "number" && Number.isFinite(hole) ? hole : null;
}

export function setLastHole(code: string, roundId: string, hole: number) {
  writeJSON(`${LAST_HOLE_PREFIX}${code}:${roundId}`, hole);
}

/** Raw read — `null` means this device has never chosen. What that absence *means* is
 * decided in `identity.ts`, which is the only place that should be reading it. */
export function getStoredMode(code: string): AppMode | null {
  const mode = readJSON<AppMode | null>(MODE_PREFIX + code, null);
  return mode === "organizer" || mode === "player" ? mode : null;
}

export function setStoredMode(code: string, mode: AppMode) {
  writeJSON(MODE_PREFIX + code, mode);
}
